import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { MessageCircle, X } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const DEFAULT_SETTINGS = {
  whatsapp_enabled: true,
  whatsapp_phone_number: '524778055212',
  whatsapp_display_number: '+52 477 805 5212',
  whatsapp_default_message: 'Hola, estoy viendo la página de KAHA y me gustaría recibir ayuda con mi pedido.',
  whatsapp_show_floating_button: true,
};

// ==================== UTILITY FUNCTIONS ====================

export const buildWhatsAppUrl = (phone, message) => {
  const encoded = encodeURIComponent(message || '');
  return `https://wa.me/${phone}${encoded ? `?text=${encoded}` : ''}`;
};

export const WHATSAPP_MESSAGES = {
  general: 'Hola, estoy viendo la página de KAHA y me gustaría recibir ayuda con mi pedido.',
  manual_quote: 'Hola, quiero solicitar una cotización personalizada para unas cajas en KAHA.',
  shipping_help: 'Hola, mi pedido requiere cotización logística personalizada y quiero apoyo para revisarlo.',
  artwork_help: 'Hola, tengo dudas sobre mi archivo o diseño para mi pedido en KAHA.',
};

export const buildQuoteContextMessage = (order) => {
  if (!order) return WHATSAPP_MESSAGES.general;
  const parts = ['Hola, estoy cotizando una caja en KAHA y quisiera apoyo.'];
  const details = [];
  if (order.dimensions) {
    details.push(`Medidas: ${order.dimensions.length}x${order.dimensions.width}x${order.dimensions.height} mm`);
  }
  if (order.material?.name) details.push(`Material: ${order.material.name}`);
  if (order.quantity) details.push(`Cantidad: ${order.quantity}`);
  if (order.total_amount) details.push(`Precio estimado: $${order.total_amount.toLocaleString('es-MX', { minimumFractionDigits: 2 })} MXN`);
  if (order.order_number) details.push(`Folio: ${order.order_number}`);
  if (details.length > 0) parts.push(`Mis datos son: ${details.join(', ')}.`);
  parts.push('¿Me pueden ayudar?');
  return parts.join(' ');
};

export const trackWhatsAppClick = async (eventType, page, orderId, context) => {
  try {
    await axios.post(`${API}/analytics/whatsapp-click`, {
      event_type: eventType,
      page,
      order_id: orderId || null,
      context: context || null,
    });
  } catch (err) { console.warn('WhatsApp analytics error:', err.message); }
};

// ==================== INLINE CTA COMPONENT ====================

export const WhatsAppCTA = ({
  message,
  label = '¿Tienes dudas? Escríbenos por WhatsApp',
  eventType = 'whatsapp_click_general',
  page = '',
  orderId = null,
  variant = 'soft', // 'soft' | 'strong'
  phone,
  className = '',
}) => {
  const [settings, setSettings] = useState(null);

  useEffect(() => {
    axios.get(`${API}/admin/whatsapp-settings`).then(r => setSettings(r.data)).catch(() => setSettings(DEFAULT_SETTINGS));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time fetch on mount; API, DEFAULT_SETTINGS are module constants
  }, []);

  if (!settings?.whatsapp_enabled) return null;

  const phoneNum = phone || settings.whatsapp_phone_number;
  const msg = message || settings.whatsapp_default_message;

  const handleClick = () => {
    trackWhatsAppClick(eventType, page, orderId);
    window.open(buildWhatsAppUrl(phoneNum, msg), '_blank', 'noopener');
  };

  if (variant === 'strong') {
    return (
      <button
        onClick={handleClick}
        className={`w-full flex items-center justify-center gap-2 p-3 rounded-lg border-2 border-green-500 bg-green-50 hover:bg-green-100 transition-colors text-green-800 font-medium text-sm ${className}`}
        data-testid="whatsapp-cta-strong"
      >
        <svg viewBox="0 0 24 24" className="w-5 h-5 fill-green-600" xmlns="http://www.w3.org/2000/svg">
          <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
        </svg>
        {label}
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      className={`inline-flex items-center gap-1.5 text-sm text-green-700 hover:text-green-800 hover:underline transition-colors ${className}`}
      data-testid="whatsapp-cta-soft"
    >
      <svg viewBox="0 0 24 24" className="w-4 h-4 fill-green-600" xmlns="http://www.w3.org/2000/svg">
        <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
      </svg>
      {label}
    </button>
  );
};

// ==================== FLOATING BUTTON COMPONENT ====================

const WhatsAppFloatingButton = () => {
  const [settings, setSettings] = useState(null);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    axios.get(`${API}/admin/whatsapp-settings`).then(r => setSettings(r.data)).catch(() => setSettings(DEFAULT_SETTINGS));
    // eslint-disable-next-line react-hooks/exhaustive-deps -- One-time fetch on mount; API, DEFAULT_SETTINGS are module constants
  }, []);

  if (!settings?.whatsapp_enabled || !settings?.whatsapp_show_floating_button) return null;

  const handleClick = (msg, eventType) => {
    trackWhatsAppClick(eventType, window.location.pathname);
    window.open(buildWhatsAppUrl(settings.whatsapp_phone_number, msg), '_blank', 'noopener');
    setOpen(false);
  };

  return (
    <div className="fixed bottom-20 right-6 z-[9999] flex flex-col items-end gap-3" data-testid="whatsapp-floating">
      {/* Expanded menu */}
      {open && (
        <div className="bg-white rounded-xl shadow-lg border border-gray-100 w-72 overflow-hidden animate-in slide-in-from-bottom-4 fade-in duration-200">
          <div className="p-4 bg-[#075E54] text-white">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-semibold text-sm">KAHA Empaque</p>
                <p className="text-xs text-green-200">Normalmente responde en minutos</p>
              </div>
              <button onClick={() => setOpen(false)} className="p-1 hover:bg-white/10 rounded transition-colors">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>
          <div className="p-3 space-y-2">
            <button
              onClick={() => handleClick(WHATSAPP_MESSAGES.general, 'whatsapp_click_general')}
              className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors group"
              data-testid="wa-option-general"
            >
              <p className="text-sm font-medium text-gray-800 group-hover:text-green-700">Ayuda general</p>
              <p className="text-xs text-gray-500 mt-0.5">Preguntas sobre productos o el proceso</p>
            </button>
            <button
              onClick={() => handleClick(WHATSAPP_MESSAGES.manual_quote, 'whatsapp_click_manual_quote')}
              className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors group"
              data-testid="wa-option-quote"
            >
              <p className="text-sm font-medium text-gray-800 group-hover:text-green-700">Cotización personalizada</p>
              <p className="text-xs text-gray-500 mt-0.5">Para pedidos especiales o volumen</p>
            </button>
            <button
              onClick={() => handleClick(WHATSAPP_MESSAGES.artwork_help, 'whatsapp_click_artwork_help')}
              className="w-full text-left p-3 rounded-lg hover:bg-gray-50 transition-colors group"
              data-testid="wa-option-artwork"
            >
              <p className="text-sm font-medium text-gray-800 group-hover:text-green-700">Ayuda con mi diseño</p>
              <p className="text-xs text-gray-500 mt-0.5">Dudas sobre archivos o arte</p>
            </button>
          </div>
          <div className="px-3 pb-3 pt-1 border-t">
            <p className="text-[10px] text-gray-400 text-center">
              {settings.whatsapp_display_number || '+52 477 805 5212'}
            </p>
          </div>
        </div>
      )}

      {/* Floating button */}
      <button
        onClick={() => setOpen(!open)}
        className="w-14 h-14 rounded-full bg-[#25D366] hover:bg-[#20BD5A] shadow-lg hover:shadow-xl transition-all duration-200 flex items-center justify-center group"
        aria-label="WhatsApp"
        data-testid="whatsapp-floating-btn"
      >
        {open ? (
          <X className="w-6 h-6 text-white" />
        ) : (
          <svg viewBox="0 0 24 24" className="w-7 h-7 fill-white" xmlns="http://www.w3.org/2000/svg">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/>
          </svg>
        )}
      </button>
    </div>
  );
};

export default WhatsAppFloatingButton;
